import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, Check, Play } from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/lib/api-client";
import { NOTIFICATION_SOUNDS, getSelectedSoundId, playNotificationSound, setSelectedSoundId } from "@/lib/notification-sound";
import { BookingAlertNative, hasNativeBookingAlarm, type AlarmDeviceStatus } from "@/lib/booking-alarm";
import { useAppStore } from "@/store/app-store";
import { Button } from "@/components/ui/Button";
import { DEFAULT_NOTIFICATION_WINDOW_MINUTES } from "@/features/notifications/useNotifications";
import type { BookingAlertMode } from "@/types/domain";

type Preferences = { notificationSound?: string; bookingAlertMode?: BookingAlertMode; notificationWindowMinutes?: number };

const LIST_WINDOWS = [
  { minutes: 15, label: "15 minutes" },
  { minutes: 30, label: "30 minutes" },
  { minutes: 60, label: "1 hour" },
  { minutes: 120, label: "2 hours" },
  { minutes: 360, label: "6 hours" },
  { minutes: 1440, label: "24 hours" },
];

const RING_MODES: { id: BookingAlertMode; label: string; hint: string }[] = [
  { id: "UNTIL_DISMISSED", label: "Ring until the popup is closed", hint: "Keeps ringing - even with the phone on silent or the app closed - until you confirm, reject or close the request, or its time runs out." },
  { id: "ONCE", label: "Play once", hint: "One short tone when a new request arrives." },
];

/** Profile → Notifications & sounds: alert tone, how new booking requests ring, and how long read notifications stay
 *  listed. Saved on the account, so all devices match. */
export function SoundSettings() {
  const user = useAppStore((state) => state.user);
  const setUser = useAppStore((state) => state.setUser);
  const [sound, setSound] = useState(getSelectedSoundId);
  const [mode, setMode] = useState<BookingAlertMode>(user?.bookingAlertMode ?? "UNTIL_DISMISSED");
  const [listWindow, setListWindow] = useState(user?.notificationWindowMinutes ?? DEFAULT_NOTIFICATION_WINDOW_MINUTES);
  const [saved, setSaved] = useState(false);
  const [device, setDevice] = useState<AlarmDeviceStatus | null>(null);

  const save = useMutation({
    mutationFn: (body: Preferences) => api<Required<Preferences>>("/notifications/preferences", { method: "PATCH", body }),
    onSuccess: (prefs) => {
      const current = useAppStore.getState().user;
      if (current) setUser({ ...current, ...prefs });
      setSelectedSoundId(prefs.notificationSound);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    },
    onError: () => {
      // Put the picker back to what the account actually has.
      setSound(getSelectedSoundId());
      setMode(useAppStore.getState().user?.bookingAlertMode ?? "UNTIL_DISMISSED");
      setListWindow(useAppStore.getState().user?.notificationWindowMinutes ?? DEFAULT_NOTIFICATION_WINDOW_MINUTES);
    },
  });

  // Things on this phone that would stop the alarm ringing with the app closed. Re-checked when the owner
  // comes back from the system settings screen.
  useEffect(() => {
    if (!hasNativeBookingAlarm()) return;
    const check = () => void BookingAlertNative.getStatus().then(setDevice).catch(() => undefined);
    check();
    document.addEventListener("visibilitychange", check);
    return () => document.removeEventListener("visibilitychange", check);
  }, []);

  const chooseSound = (id: string) => {
    setSound(id);
    playNotificationSound(id);
    save.mutate({ notificationSound: id });
  };
  const chooseMode = (id: BookingAlertMode) => {
    setMode(id);
    save.mutate({ bookingAlertMode: id });
  };
  const chooseListWindow = (minutes: number) => {
    setListWindow(minutes);
    save.mutate({ notificationWindowMinutes: minutes });
  };

  const deviceIssues = device && mode === "UNTIL_DISMISSED"
    ? [
        !device.notificationsEnabled && { text: "Notifications are turned off for this app.", action: "Turn on", run: () => BookingAlertNative.openNotificationSettings() },
        !device.batteryUnrestricted && { text: "Battery saver may stop the alarm while the app is closed.", action: "Allow", run: () => BookingAlertNative.requestBatteryUnrestricted() },
        !device.fullScreenAllowed && { text: "The alarm can't pop up full screen.", action: "Allow", run: () => BookingAlertNative.openFullScreenSettings() },
      ].filter((issue): issue is { text: string; action: string; run: () => Promise<void> } => Boolean(issue))
    : [];

  return (
    <div className="sound-settings">
      <fieldset className="sound-settings__group">
        <legend>New booking request</legend>
        <div className="notification-sound__list" role="radiogroup">
          {RING_MODES.map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={mode === option.id}
              className={clsx("notification-sound__row sound-settings__mode", mode === option.id && "is-selected")}
              onClick={() => chooseMode(option.id)}
            >
              <span className="notification-sound__check">{mode === option.id && <Check size={14} />}</span>
              <span>
                <strong>{option.label}</strong>
                <small>{option.hint}</small>
              </span>
            </button>
          ))}
        </div>
      </fieldset>

      {deviceIssues.length > 0 && (
        <div className="sound-settings__issues" role="status">
          {deviceIssues.map((issue) => (
            <div key={issue.text} className="sound-settings__issue">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>{issue.text}</span>
              <Button type="button" variant="secondary" onClick={() => void issue.run()}>{issue.action}</Button>
            </div>
          ))}
        </div>
      )}

      <fieldset className="sound-settings__group">
        <legend>Alert tone</legend>
        <div className="notification-sound__list" role="radiogroup">
          {NOTIFICATION_SOUNDS.map((option) => (
            <div key={option.id} className={clsx("notification-sound__row", sound === option.id && "is-selected")}>
              <button type="button" role="radio" aria-checked={sound === option.id} className="notification-sound__pick" onClick={() => chooseSound(option.id)}>
                <span className="notification-sound__check">{sound === option.id && <Check size={14} />}</span>
                <span>{option.label}</span>
              </button>
              <button
                type="button"
                className="notification-sound__preview"
                aria-label={`Preview ${option.label}`}
                onClick={() => playNotificationSound(option.id)}
              >
                <Play size={14} />
              </button>
            </div>
          ))}
        </div>
      </fieldset>

      <fieldset className="sound-settings__group">
        <legend>Notification list</legend>
        <label className="sound-settings__window">
          <span>Unread notifications always show. Keep read ones for</span>
          <select value={listWindow} onChange={(event) => chooseListWindow(Number(event.target.value))}>
            {LIST_WINDOWS.map((option) => <option key={option.minutes} value={option.minutes}>{option.label}</option>)}
          </select>
        </label>
      </fieldset>

      {save.error && <div className="form-error">{save.error.message}</div>}
      <footer className="sound-settings__footer" aria-live="polite">
        {save.isPending && <small>Saving…</small>}
        {saved && <span className="settings-saved">Saved</span>}
      </footer>
    </div>
  );
}
